import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import Icon from '../../shared/components/Icons';

const PASSWORD_HINT = 'At least 10 characters, with an uppercase letter, a lowercase letter, a number and a symbol.';

function PasswordInput(props) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="password-field">
      <input {...props} type={visible ? 'text' : 'password'} />
      <button
        type="button"
        className="password-toggle"
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        onClick={() => setVisible((current) => !current)}
      >
        <Icon name={visible ? 'eyeOff' : 'eye'} size={18} />
      </button>
    </span>
  );
}

const titles = {
  login: 'Welcome back',
  register: 'Create your account',
  'forgot-password': 'Reset your password',
  'reset-password': 'Choose a new password',
};

export default function AccountPage() {
  const { mode = 'login' } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: searchParams.get('email') || '',
    password: '',
    newPassword: '',
    token: searchParams.get('token') || '',
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const change = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (mode === 'register') {
        await auth.register({
          email: form.email,
          password: form.password,
          firstName: form.firstName,
          lastName: form.lastName,
        });
        navigate('/');
      } else if (mode === 'login') {
        await auth.login(form.email, form.password);
        navigate(searchParams.get('returnTo') || '/');
      } else if (mode === 'forgot-password') {
        const response = await auth.forgotPassword(form.email);
        setMessage(response.message);
      } else if (mode === 'reset-password') {
        const response = await auth.resetPassword(form.email, form.token, form.newPassword);
        setMessage(response.message);
      } else {
        setError('This account page is not available.');
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="account-page container">
      <form className="account-card" onSubmit={submit}>
        <Link to="/" className="link">← Back to Trawtel</Link>
        <h1>{titles[mode] || 'Account'}</h1>
        <p className="muted">Sign in to manage your Trawtel account.</p>

        {mode === 'register' && (
          <div className="account-name-fields">
            <label>First name<input required maxLength="100" autoComplete="given-name" value={form.firstName} onChange={change('firstName')} /></label>
            <label>Last name<input required maxLength="100" autoComplete="family-name" value={form.lastName} onChange={change('lastName')} /></label>
          </div>
        )}

        {mode !== 'reset-password' && (
          <label>Email address
            <input required type="email" maxLength="256" autoComplete="email" placeholder="you@example.com" value={form.email} onChange={change('email')} />
          </label>
        )}

        {mode === 'reset-password' && (
          <>
            <label>Email address<input required type="email" value={form.email} onChange={change('email')} /></label>
            <label>Reset token<input required value={form.token} onChange={change('token')} /></label>
          </>
        )}

        {mode === 'login' && (
          <label>Password<PasswordInput required autoComplete="current-password" value={form.password} onChange={change('password')} /></label>
        )}
        {mode === 'register' && (
          <label>Password<PasswordInput required minLength="10" maxLength="128" autoComplete="new-password" value={form.password} onChange={change('password')} /><small className="muted">{PASSWORD_HINT}</small></label>
        )}
        {mode === 'reset-password' && (
          <label>New password<PasswordInput required minLength="10" maxLength="128" autoComplete="new-password" value={form.newPassword} onChange={change('newPassword')} /><small className="muted">{PASSWORD_HINT}</small></label>
        )}

        {error && <p className="api-message error" role="alert">{error}</p>}
        {message && <p className="api-message success" role="status">{message}</p>}
        {mode !== 'reset-password' && mode !== 'forgot-password' && (
          <button className="btn btn-primary block" disabled={busy}>{busy ? 'Please wait…' : mode === 'register' ? 'Create account' : 'Sign in'}</button>
        )}
        {(mode === 'forgot-password' || mode === 'reset-password') && (
          <button className="btn btn-primary block" disabled={busy}>{busy ? 'Please wait…' : mode === 'forgot-password' ? 'Send reset instructions' : 'Reset password'}</button>
        )}

        <div className="account-links">
          {mode === 'login' && <><Link to="/account/forgot-password">Forgot password?</Link><Link to="/account/register">Create an account</Link></>}
          {mode === 'register' && <Link to="/account/login">Already have an account? Sign in</Link>}
          {(mode === 'forgot-password' || mode === 'reset-password') && <Link to="/account/login">Back to sign in</Link>}
        </div>
      </form>
    </section>
  );
}
